import { MainApi } from 'api/MainApi'
import { Button } from 'components/Button/Button'
import Dropdown, { IOption } from 'components/Dropdown/Dropdown'
import Title from 'components/Title/Title'
import { useEffectOnce } from 'hooks/useEffectOnce'
import { useEffect, useMemo, useRef, useState } from 'react'
import Modal from 'react-modal'
import type { IFacility, IRobotMap } from 'utils/interfaces'

import style from './LocationSelectModal.module.css'

interface IProps {
  isOpen: boolean
  title?: string
  currentLocation?: string | null
  onClose: () => void
  onConfirm: (map: IRobotMap) => void
}

const LocationSelectModal = ({ isOpen, title = 'Select Location', currentLocation, onClose, onConfirm }: IProps) => {
  const [facilities, setFacilities] = useState<IFacility[]>([])
  const [selectedFacilityId, setSelectedFacilityId] = useState<number | null>(null)
  const [maps, setMaps] = useState<IRobotMap[]>([])
  const [selectedLocation, setSelectedLocation] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const allMapsRef = useRef<IRobotMap[]>([])
  const skipNextFetch = useRef(false)

  useEffect(() => {
    if (isOpen && document.fullscreenElement) {
      document.exitFullscreen().catch(() => {})
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen) {
      setSelectedLocation(currentLocation || '')
    }
  }, [isOpen, currentLocation])

  useEffectOnce(() => {
    setLoading(true)
    Promise.all([MainApi.fetchFacilities(1, 1000), MainApi.fetchRobotMaps(1, 1000)])
      .then(([facilitiesRes, mapsRes]) => {
        allMapsRef.current = mapsRes.results
        const facilityIdsWithMaps = new Set(mapsRes.results.map(m => m.facility_id).filter(id => id !== null))
        const filteredFacilities = facilitiesRes.results.filter(f => facilityIdsWithMaps.has(f.id))
        setFacilities(filteredFacilities)

        if (currentLocation) {
          const map = mapsRes.results.find(m => m.name === currentLocation)
          if (map?.facility_id) {
            skipNextFetch.current = true
            setSelectedFacilityId(map.facility_id)
            setSelectedLocation(currentLocation)
            setMaps(mapsRes.results.filter(m => m.facility_id === map.facility_id))
          }
        }
      })
      .finally(() => setLoading(false))
  })

  useEffect(() => {
    if (selectedFacilityId) {
      if (skipNextFetch.current) {
        skipNextFetch.current = false
        return
      }
      setLoading(true)
      MainApi.fetchRobotMaps(undefined, 1000, undefined, selectedFacilityId)
        .then(res => {
          setMaps(res.results)
        })
        .finally(() => setLoading(false))
    } else {
      setMaps([])
    }
  }, [selectedFacilityId])

  const facilityOptions: IOption[] = useMemo(
    () => facilities.map(f => ({ text: f.name, value: String(f.id) })),
    [facilities]
  )

  const facilityOption = useMemo(
    () => facilityOptions.find(o => o.value === String(selectedFacilityId)),
    [facilityOptions, selectedFacilityId]
  )

  const locationOptions: IOption[] = useMemo(() => maps.map(m => ({ text: m.name, value: m.name })), [maps])

  const locationOption = useMemo(
    () => locationOptions.find(o => o.value === selectedLocation),
    [locationOptions, selectedLocation]
  )

  const onFacilitySelected = (opt: IOption) => {
    setSelectedFacilityId(Number(opt.value))
    setSelectedLocation('')
  }

  const onLocationSelected = (opt: IOption) => {
    setSelectedLocation(opt.value as string)
  }

  const onConfirmClick = () => {
    const map = maps.find(m => m.name === selectedLocation)
    if (map) onConfirm(map)
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className={style.root}
      style={{ overlay: { zIndex: 110, backgroundColor: 'rgba(27, 43, 72, 0.6)' } }}
      ariaHideApp={false}
    >
      <div className={style.modal}>
        <Title text={title} className={style.title} />
        <div className={style.content}>
          <Dropdown
            className={style.dropdown}
            description="Facility"
            options={facilityOptions}
            currentOption={facilityOption}
            onClickOption={onFacilitySelected}
            disabled={loading && facilities.length === 0}
            placeholder={loading && facilities.length === 0 ? 'Loading facilities...' : 'Select facility'}
          />
          <div className={style.spacer} />
          <Dropdown
            className={style.dropdown}
            description="Robot Map"
            options={locationOptions}
            currentOption={locationOption}
            onClickOption={onLocationSelected}
            disabled={!selectedFacilityId || loading}
            placeholder={loading ? 'Loading maps...' : 'Select map'}
          />
        </div>
        <div className={style.footer}>
          <Button.Outlined className={style.btn} onClick={onClose}>
            Cancel
          </Button.Outlined>
          <Button.Standart className={style.btn} onClick={onConfirmClick} disabled={!locationOption}>
            Select
          </Button.Standart>
        </div>
      </div>
    </Modal>
  )
}

export default LocationSelectModal
